import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Paper,
    LinearProgress,
    Button,
    Avatar,
    Stack,
    Chip,
    Skeleton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ToggleButtonGroup,
    ToggleButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StarIcon from "@mui/icons-material/Star";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import SchoolIcon from "@mui/icons-material/School";
import SortIcon from "@mui/icons-material/Sort";
import FilterListIcon from "@mui/icons-material/FilterList";
import { POST } from "api/index";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import ImageComponent from "ui/components/shared/ImageComponent";
import ComingSoonDialog from "ui/components/shared/ComingSoonDialog";
import useAlert from 'hooks/AlertHook';
import { AiOutlineSortAscending, AiOutlineSortDescending } from "react-icons/ai";
import { GoSortAsc, GoSortDesc } from "react-icons/go";
import { Tooltip, IconButton } from "@mui/material";


// Responsive course cards
const MobileCourseCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: 16,
    marginBottom: theme.spacing(2),
    background: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2.5),
        borderRadius: 20,
        marginBottom: theme.spacing(2.5),
    },
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
    }
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        transition: 'transform 0.4s ease'
    },
    [theme.breakpoints.up('sm')]: {
        height: 10,
        borderRadius: 5,
        '& .MuiLinearProgress-bar': {
            borderRadius: 5,
        }
    }
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 'bold',
    padding: theme.spacing(1, 2.5),
    fontSize: '0.85rem',
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1.5, 3),
        fontSize: '0.9rem',
    },
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transform: 'translateY(-1px)'
    }
}));

interface Course {
    name: string;
    id: number;
    progress: number;
    points: string;
    icon: string;
}

type SortField = 'name' | 'progress';
type SortOrder = 'asc' | 'desc';
type SortOption = 'name-asc' | 'name-desc' | 'progress-asc' | 'progress-desc';

type FilterOption = 'all' | 'active' | 'inactive';

const MobileCourses: React.FC = () => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [sortBy, setSortBy] = useState<SortOption>('progress-asc');
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [filterBy, setFilterBy] = useState<FilterOption>('all');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const token = window.localStorage.getItem("currentUserToken");

    const bookCourse = async (courseId: number) => {
        const body = new URLSearchParams();
        if (token) body.append('userToken', token);
        body.append('option', courseId.toString());

        const response = await POST('addUserToCourse', body);
        if (response.status === 200) {
            setAlert({ color: 'success', message: t("Course added to MySkills again!") });
        } else {
            setAlert({ color: 'error', message: response.error });
        }

        return {
            status: response.status,
            content: response.content,
            message: response.error || '',
        };
    };

const handleNavigateToCourse = async (courseId: number) => {
  try {
    localStorage.setItem("lastBookedCourse", String(courseId));
    
    const mySkills = JSON.parse(localStorage.getItem("mySkills") || "[]");
    const alreadyBooked = mySkills.includes(courseId);

    if (!alreadyBooked) {
      const resp = await bookCourse(courseId);
      if (resp.status !== 200) {
        console.error("Kurs konnte nicht gebucht werden:", resp);
        return;
      }
      
      // WICHTIG: MySkills sofort aktualisieren
      const updatedSkills = [...mySkills, courseId];
      localStorage.setItem("mySkills", JSON.stringify(updatedSkills));
    }

    // Erst nach erfolgreichem Booking navigieren
    navigate(`/course/${courseId}/chat`);
  } catch (error) {
    console.error("Fehler beim Kursstart:", error);
  }
};

    const getCourses = async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const body = new URLSearchParams();
            body.append("userToken", token);
            const response = await POST("getCourses", body);

            if (response.status === 200) {
                const formattedCourses = response.result.map((course: any) => ({
                    name: course.title,
                    id: course.id,
                    progress: 0,
                    points: "0/0",
                    icon: course.image || "default-course-icon.png",
                }));

                setCourses(formattedCourses);

                // Fetch progress for each course
                const pointsRequests = formattedCourses.map(async (course: Course) => {
                    const pointsBody = new URLSearchParams();
                    pointsBody.append("userToken", token);
                    pointsBody.append("courseId", course.id.toString());

                    const [pointsResponse, userPointsResponse] = await Promise.all([
                        POST("getCoursePoints", pointsBody),
                        POST("getUserCoursePoints", pointsBody)
                    ]);

                    return {
                        id: course.id,
                        maxPoints: pointsResponse?.total_points || 100,
                        userPoints: userPointsResponse?.course_progress?.progress || 0,
                    };
                });

                const pointsResults = await Promise.all(pointsRequests);
                setCourses((prevCourses) =>
                    prevCourses.map((course) => {
                        const pointsData = pointsResults.find((p) => p.id === course.id);
                        return pointsData
                            ? {
                                ...course,
                                progress: Math.min((pointsData.userPoints / pointsData.maxPoints) * 100, 100),
                                points: `${pointsData.userPoints}/${pointsData.maxPoints}`,
                            }
                            : course;
                    })
                );
            } else {
                console.error("Fehler beim Abrufen der Kurse:", response);
            }
        } catch (error) {
            console.error("API-Fehler:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCourses();
    }, []);

    // Sort and filter courses
    const filteredAndSortedCourses = useMemo(() => {
        let filtered = courses;
        if (filterBy === 'active') filtered = courses.filter(c => c.progress > 0);
        else if (filterBy === 'inactive') filtered = courses.filter(c => c.progress === 0);

        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc': return a.name.localeCompare(b.name, 'de');
                case 'name-desc': return b.name.localeCompare(a.name, 'de');
                case 'progress-asc': return a.progress - b.progress;
                case 'progress-desc': return b.progress - a.progress;
                default: return 0;
            }
        });

        return sorted;
    }, [courses, sortBy, filterBy]);


    const getProgressColor = (progress: number) => {
        if (progress >= 75) return "#4CAF50";
        if (progress >= 50) return "";
        if (progress >= 25) return "#FF9800";
        return "#F44336";
    };

    const getProgressLabel = (progress: number) => {
        if (progress === 0) return t("skillboard.courses.newStart");
        if (progress >= 100) return t("skillboard.courses.completed");
        if (progress >= 80) return t("skillboard.courses.almostDone");
        if (progress >= 50) return t("skillboard.courses.continue");
        return t("skillboard.courses.keepLearning");
    };

    if (loading) {
        return (
            <Box sx={{ px: { xs: 1, sm: 0 } }}>
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                        mb: 2.5,
                        color: '#1a1a1a',
                        fontSize: { xs: '1.4rem', sm: '1.6rem' },
                    }}
                >
                    {t("skillboard.courses.title")}
                </Typography>

                <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Paper key={i} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Skeleton variant="rectangular" width={60} height={60} sx={{ borderRadius: 2 }} />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Skeleton variant="text" width="80%" height={24} />
                                    <Skeleton variant="text" width="60%" height={20} />
                                </Box>
                            </Box>
                            <Skeleton variant="rectangular" height={8} sx={{ mb: 2, borderRadius: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Skeleton variant="rectangular" height={36} sx={{ flexGrow: 1, borderRadius: 3 }} />
                                <Skeleton variant="rectangular" width={70} height={36} sx={{ borderRadius: 3 }} />
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                    mb: 2.5,
                    color: '#1a1a1a',
                    fontSize: { xs: '1.4rem', sm: '1.6rem' },
                }}
            >
                {t("skillboard.courses.title")}
            </Typography>

            {/* Sorting and Filtering Controls */}
            {courses.length > 0 && (
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}
                >
                    {/* Sort-Kriterium */}
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
                        <InputLabel id="sort-label"> {t("skillboard.courses.sorting")}</InputLabel>
                        <Select
                            labelId="sort-label"
                            value={sortBy}
                            label={t("skillboard.courses.filter")}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            renderValue={(val) => {
                                const map: Record<SortOption, { icon: JSX.Element; text: string }> = {
                                    'name-asc': { icon: <AiOutlineSortAscending size={18} />, text: t("skillboard.courses.sortNameAZ") },
                                    'name-desc': { icon: <AiOutlineSortDescending size={18} />, text: t("skillboard.courses.sortNameZA") },
                                    'progress-asc': { icon: <GoSortAsc size={18} />, text: t("skillboard.courses.sortProgressAsc") },
                                    'progress-desc': { icon: <GoSortDesc size={18} />, text: t("skillboard.courses.sortProgressDesc") },
                                };
                                const { icon, text } = map[val as SortOption];
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {icon}<span>{text}</span>
                                    </Box>
                                );
                            }}
                            MenuProps={{ PaperProps: { elevation: 3 } }}
                        >
                            <MenuItem value="name-asc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AiOutlineSortAscending size={18} />
                                    <Typography variant="body2">{t("skillboard.courses.sortNameAZ")}</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="name-desc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AiOutlineSortDescending size={18} />
                                    <Typography variant="body2">{t("skillboard.courses.sortNameZA")}</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="progress-asc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GoSortAsc size={18} />
                                    <Typography variant="body2">{t("skillboard.courses.sortProgressAsc")}</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="progress-desc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GoSortDesc size={18} />
                                    <Typography variant="body2">{t("skillboard.courses.sortProgressDesc")}</Typography>
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>


                    {/* Filter-Gruppe (unverändert, nur minimal getuned) */}
                    <ToggleButtonGroup
                        value={filterBy}
                        exclusive
                        onChange={(_, v) => v && setFilterBy(v)}
                        size="small"
                        sx={{
                            ml: { sm: 'auto' },
                            '& .MuiToggleButton-root': {
                                px: { xs: 2, sm: 3 },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }
                        }}
                    >
                        <ToggleButton value="all">{t("skillboard.courses.filterAll")}({courses.length})</ToggleButton>
                        <ToggleButton value="active">{t("skillboard.courses.filterActive")} ({courses.filter(c => c.progress > 0).length})</ToggleButton>
                        <ToggleButton value="inactive">{t("skillboard.courses.filterInactive")} ({courses.filter(c => c.progress === 0).length})</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {courses.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t("skillboard.courses.noCourses")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("skillboard.courses.noCoursesDescription")}

                    </Typography>
                </Paper>
            ) : filteredAndSortedCourses.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t("skillboard.courses.noCoursesFound")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("skillboard.courses.noCoursesFoundDescription")}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    {filteredAndSortedCourses.map((course) => (
                        <MobileCourseCard key={course.id}>
                            {/* Course Header */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: { xs: 1.5, sm: 2 },
                                mb: { xs: 1.5, sm: 2 }
                            }}>
                                {course.icon && course.icon !== "default-course-icon.png" ? (
                                    <ImageComponent
                                        src={course.icon}
                                        alt={course.name}
                                        width="60px"
                                        height="60px"
                                        borderRadius="12px"
                                    />
                                ) : (
                                    <Avatar sx={{
                                        width: 60,
                                        height: 60,
                                        bgcolor: 'primary.light',
                                        color: 'primary.main'
                                    }}>
                                        <AutoStoriesIcon />
                                    </Avatar>
                                )}

                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            mb: 1,
                                            fontSize: { xs: '1rem', sm: '1.1rem' },
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleNavigateToCourse(course.id)}
                                    >
                                        {course.name}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <StarIcon sx={{ color: "#FFD700", fontSize: 16 }} />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                            >
                                                {course.points} {t("skillboard.courses.points")}
                                            </Typography>
                                        </Box>

                                        {course.progress == 0 && (
                                            <Chip
                                                label={t("skillboard.courses.filterInactive")}
                                                size="small"
                                                sx={{
                                                    bgcolor: '',
                                                    color: 'success.main',
                                                    fontSize: '0.7rem',
                                                    height: 20
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Progress Section */}
                            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1
                                }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                    >
                                        {t("skillboard.courses.progress")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                    >
                                        {Math.round(course.progress)}%
                                    </Typography>
                                </Box>
                                <ProgressBar
                                    variant="determinate"
                                    value={course.progress}
                                    sx={{
                                        cursor: 'pointer',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: getProgressColor(course.progress),
                                        },
                                    }}
                                    onClick={() => handleNavigateToCourse(course.id)}
                                />
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
                                <ActionButton
                                    variant="contained"
                                    fullWidth
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => handleNavigateToCourse(course.id)}
                                    sx={{ bgcolor: 'primary.main', color: 'white' }}
                                >
                                    {getProgressLabel(course.progress)}
                                </ActionButton>
                                <ActionButton
                                    variant="outlined"
                                    onClick={() => setOpen(true)}
                                    sx={{
                                        minWidth: 'auto',

                                        borderColor: 'primary.main',
                                        color: 'primary.main'
                                    }}
                                >
                                    {t("skillboard.courses.cards")}
                                </ActionButton>
                            </Box>
                        </MobileCourseCard>
                    ))}
                </Stack>
            )}

            <ComingSoonDialog open={open} onClose={() => setOpen(false)} />
        </Box>
    );
};

export default MobileCourses;